/**
 * SecurityPolicyCard — Owner/Control toggle for the global 2FA policy.
 *
 * The 2FA challenge in `Login.jsx` short-circuits to `requires_2fa:false`
 * when this flag is off (default). Flip it on once every supervisor
 * confirms they're receiving OTP emails.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, ShieldOff, Loader2, Check, AlertTriangle } from "lucide-react";
import { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PORTAL_BACKEND = process.env.REACT_APP_BACKEND_URL;
const portalApi = axios.create({ baseURL: PORTAL_BACKEND });

// Roles allowed to flip the security policy toggle.
const ALLOWED = new Set(["owner", "control", "admin", "superintendent"]);

export default function SecurityPolicyCard() {
  const { user } = useAuth();
  const role = String(user?.role || "").toLowerCase();
  const canEdit = ALLOWED.has(role) || Boolean(user?.is_owner);
  const [policy, setPolicy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  const load = async () => {
    try {
      const { data } = await portalApi.get("/api/admin/security-policy");
      setPolicy(data);
    } catch (ex) {
      setErr(apiError(ex));
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key) => {
    if (!canEdit || busy || !policy) return;
    const next = { ...policy, [key]: !policy[key] };
    setBusy(true); setErr("");
    try {
      const actor = user?.username || user?.name || "portal";
      await portalApi.put("/api/admin/security-policy", { [key]: next[key] }, {
        headers: { "X-Portal-Actor": actor },
      });
      setPolicy(next);
      setSavedAt(new Date());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (ex) {
      setErr(apiError(ex));
    } finally { setBusy(false); }
  };

  if (!canEdit) return null;

  return (
    <div className="panel-flat p-4" data-testid="security-policy-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
             style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
          <ShieldCheck className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted">Owner · Security</div>
          <div className="font-display font-black text-base tracking-tight">Security Policy</div>
        </div>
        {savedAt && (
          <span className="pill pill-green !text-[10px] !py-0.5 !px-2" data-testid="policy-saved">
            <Check className="w-3 h-3" /> Saved
          </span>
        )}
      </div>

      {err && (
        <div className="pill pill-red !text-xs !py-2 !px-3 justify-start mb-3">
          <AlertTriangle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      {policy === null ? (
        <div className="flex items-center gap-2 text-muted text-sm py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading policy…
        </div>
      ) : (
        <div className="space-y-2">
          <PolicyRow
            label="Two-factor authentication (email OTP)"
            hint="When ON, supervisors and above must clear a 6-digit email code every 3 days. When OFF, users sign in with PIN alone."
            enabled={policy.two_factor_enabled}
            busy={busy}
            testid="policy-2fa"
            onToggle={() => toggle("two_factor_enabled")}
          />
          <PolicyRow
            label="Verify supervisor emails at signup"
            hint="Requires new Supervisor / Superintendent / Owner registrations to confirm their email via OTP before the account is created."
            enabled={policy.require_email_verified_supervisor_signup}
            busy={busy}
            testid="policy-signup-verify"
            onToggle={() => toggle("require_email_verified_supervisor_signup")}
          />
        </div>
      )}
    </div>
  );
}

function PolicyRow({ label, hint, enabled, busy, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={busy}
      className="w-full text-left flex items-start gap-3 p-3 rounded-lg transition-colors"
      style={{
        background: enabled ? "rgba(245,130,32,0.10)" : "rgba(255,255,255,0.03)",
        border: `1px solid ${enabled ? "rgba(245,130,32,0.35)" : "rgba(255,255,255,0.08)"}`,
      }}
      data-testid={testid}
    >
      <div className="mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
           style={{ background: enabled ? "rgba(245,130,32,0.20)" : "rgba(255,255,255,0.05)",
                    color: enabled ? "var(--primary)" : "rgba(255,255,255,0.5)" }}>
        {enabled ? <ShieldCheck className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-black">{label}</div>
        <div className="text-[11px] text-muted mt-0.5 leading-relaxed">{hint}</div>
      </div>
      <div className="flex items-center gap-2 pt-0.5">
        <div
          className="w-11 h-6 rounded-full relative transition-colors flex-shrink-0"
          style={{ background: enabled ? "var(--primary)" : "rgba(255,255,255,0.15)" }}
        >
          <div
            className="absolute top-0.5 w-5 h-5 rounded-full transition-all"
            style={{
              left: enabled ? "20px" : "2px",
              background: "#0a0a0a",
              boxShadow: "0 1px 2px rgba(0,0,0,0.5)",
            }}
          />
        </div>
      </div>
    </button>
  );
}
